import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Box from '@mui/material/Box';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { useBasket } from '@features/basket/hooks/useBasket';
import {
  resolveEffectivePeriod,
  resolveEffectivePrice,
  hasValidPricing,
  usePeriodStore,
  currencySymbol,
} from '@shared/pricing';
import { productTypeColor, productTypeLabel } from '@shared/constants/product';
import { coverageLabel } from '@shared/constants/coverage';
import type { Holding, HoldingStatus } from '../types';

function formatDate(iso?: string): string {
  if (!iso) return 'No Date';
  return iso.slice(0, 10); // YYYY-MM-DD
}

function expiryColor(status: HoldingStatus): string {
  if (status === 'expiring') return 'warning.main';
  if (status === 'expired') return 'error.main';
  return 'text.primary';
}

function noDateColor(): string {
  return 'error.main';
}

interface Props {
  holdings: Holding[];
  loading?: boolean;
  onSelectHolding: (holding: Holding) => void;
  onDoubleClickHolding?: (holding: Holding) => void;
  selectedProductId?: string | null;
}

export default function HoldingsList({
  holdings,
  loading = false,
  onSelectHolding,
  onDoubleClickHolding,
  selectedProductId,
}: Props) {
  const addItem = useBasket((s) => s.addItem);
  const basketItems = useBasket((s) => s.items);
  const requestedPeriod = usePeriodStore((s) => s.requestedPeriodMonths);

  function handleAddToBasket(e: React.MouseEvent, holding: Holding) {
    e.stopPropagation();
    const prices = holding.prices ?? [];
    const effectivePeriod = resolveEffectivePeriod(prices, requestedPeriod);
    const effectivePrice = resolveEffectivePrice(prices, requestedPeriod);
    addItem({
      productId: holding.productId,
      productType: holding.productType,
      name: holding.name,
      title: holding.title,
      coverage: holding.coverage,
      licensePeriodMonths: effectivePeriod,
      priceAmount: effectivePrice?.priceAmount,
      currencyCode: effectivePrice?.currencyCode,
      availablePrices: prices.length > 0 ? prices : undefined,
      source: 'holdings',
      geometry: holding.geometry,
    });
  }

  return (
    <div data-testid="holdings-list">
      <List disablePadding>
        {loading ? (
          Array.from({ length: 5 }, (_, i) => (
            <Box key={i} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          ))
        ) : holdings.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            No holdings found
          </Typography>
        ) : (
          holdings.map((holding) => (
            <HoldingCard
              key={holding.productId}
              holding={holding}
              selected={selectedProductId === holding.productId}
              inBasket={basketItems.some((i) => i.productId === holding.productId)}
              onClick={() => onSelectHolding(holding)}
              onDoubleClick={() => onDoubleClickHolding?.(holding)}
              onAddToBasket={(e) => handleAddToBasket(e, holding)}
            />
          ))
        )}
      </List>
    </div>
  );
}

interface HoldingCardProps {
  holding: Holding;
  selected: boolean;
  inBasket: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
  onAddToBasket: (e: React.MouseEvent) => void;
}

function HoldingCard({
  holding,
  selected,
  inBasket,
  onClick,
  onDoubleClick,
  onAddToBasket,
}: HoldingCardProps) {
  const showCart = holding.status === 'expiring' || holding.status === 'expired';
  const hasExpiry = !!holding.expiryDate;

  const requestedPeriod = usePeriodStore((s) => s.requestedPeriodMonths);
  const prices = holding.prices ?? [];
  const effectivePrice =
    showCart && hasValidPricing(prices)
      ? resolveEffectivePrice(prices, requestedPeriod)
      : undefined;

  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      sx={{
        alignItems: 'flex-start',
        gap: 1,
        py: 1,
        px: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {holding.status !== 'active' && (
            <Tooltip title={holding.status === 'expiring' ? 'Expiring' : 'Expired'}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: holding.status === 'expiring' ? 'warning.main' : 'error.main',
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
          <Box
            sx={{
              px: 0.5,
              py: 0.125,
              borderRadius: 0.5,
              bgcolor: productTypeColor[holding.productType],
              color: 'common.white',
              fontSize: '0.6rem',
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {productTypeLabel[holding.productType]}
          </Box>
          <Typography variant="h6" noWrap sx={{ fontSize: '1rem', lineHeight: 1.4 }}>
            {holding.name}
          </Typography>
        </Box>
        {holding.title && (
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {holding.title}
          </Typography>
        )}
        {holding.productType === 'AVCS' && coverageLabel(holding.coverage) && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ fontSize: '0.65rem' }}
          >
            {coverageLabel(holding.coverage)}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.25,
          pt: 0.25,
        }}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: hasExpiry ? expiryColor(holding.status) : noDateColor(),
          }}
        >
          {formatDate(holding.expiryDate)}
        </Typography>

        {inBasket ? (
          <Typography variant="caption" color="text.secondary">
            In basket
          </Typography>
        ) : showCart ? (
          <Tooltip title="Add to basket">
            <IconButton size="small" onClick={onAddToBasket} color="primary">
              <AddShoppingCartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}

        {effectivePrice && !inBasket && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontWeight: 600 }}>
            {currencySymbol(effectivePrice.currencyCode)}
            {effectivePrice.priceAmount.toFixed(2)}
          </Typography>
        )}
      </Box>
    </ListItemButton>
  );
}
